<script setup lang="ts">
import { PropType, watch } from 'vue'

/* Stores */
import { useBasketStore } from '~/store/basket/basket'

/* Components */
import BasketDeliveryAddressTag from '~/components/Basket/basket-addresses/BasketDeliveryAddressTag.vue'
import IconChevronDown from '~/components/Icons/IconChevronDown.vue'
import IconClose from '~/components/Icons/IconClose.vue'
import IconCircle from '~/components/Icons/IconCircle.vue'
import IconRing from '~/components/Icons/IconRing.vue'
import AppButton from '~/components/App/AppButton.vue'

/* Types */
import { Address } from '~/types/store/addresses'
import { DeliveryMethods } from '~/api/modules/basket/types'
import { Cost } from '~/types/api/api-v3/basket'

const props = defineProps({
  maxCountOfVisibleAddresses: {
    type: Number,
    required: true,
  },
  addresses: {
    type: Array as PropType<Address[]>,
    required: true,
  },
  modelValue: {
    type: Object as PropType<Address>,
    required: false,
    default: null,
  },
  deliveryCost: {
    required: false,
    default: null,
    type: Object as PropType<Cost | null>,
  },
})

const emit = defineEmits([
  'update:model-value',
  'openDialogNotification',
  'openEditAddressDialog',
  'openDeleteAddressDialog',
])

const basketStore = useBasketStore()

const { isMobile, isDesktop } = useDevice()

const showAllAddresses = ref(false)

const activeAddress = ref<Address | null>(null)

const countOfVisibleAddresses = computed(() =>
  showAllAddresses.value
    ? props.addresses.length
    : props.maxCountOfVisibleAddresses,
)

const showAllButtonVisible = computed(
  () => props.addresses.length > props.maxCountOfVisibleAddresses,
)

const deliveryMethod = computed(() => basketStore.data.delivery.method)

watch(
  () => props.addresses,
  (addresses) => {
    if (!props.modelValue || addresses.length === 0) return

    const valueIsNotIncludedInAddresses = addresses.every(
      (address) => address.id !== props.modelValue.id,
    )

    if (valueIsNotIncludedInAddresses) {
      change(null)
    }

    const valueHidden = props.addresses
      .slice(0, props.maxCountOfVisibleAddresses)
      .every((address) => address.id !== props.modelValue.id)

    if (!showAllAddresses.value && props.modelValue && valueHidden)
      showAllAddresses.value = true
  },
  {
    immediate: true,
  },
)

const change = (value: Address | null) => {
  // проверяем является ли это адрес и находится в зоне доставки
  if (!value || ('is_in_delivery_area' in value && !value.is_in_delivery_area))
    return

  emit('update:model-value', value)

  // обновляем локально активный адрес
  activeAddress.value = value
}

watch(
  () => props.modelValue,
  (address) => {
    activeAddress.value = address
  },
  {
    immediate: true,
  },
)

const toggleShowAllAddresses = () => {
  showAllAddresses.value = !showAllAddresses.value
}

const detectedClassRadio = (index: number) => {
  return {
    'radio--no-visible': index + 1 > countOfVisibleAddresses.value,
    'radio--last':
      index + 1 === countOfVisibleAddresses.value ||
      index + 1 === props.addresses.length,
  }
}

// Открываем блок уведомления по клику, если адрес не активен
const handleNotDeliveryAddress = (event: Event, address: Address) => {
  if ('is_in_delivery_area' in address && !address.is_in_delivery_area) {
    emit('openDialogNotification')
    event.preventDefault()
  }
}

const openEditAddressDialog = (address: Address) => {
  emit('openEditAddressDialog', address)
}

const openDeleteAddressDialog = (address: Address) => {
  emit('openDeleteAddressDialog', address)
}

const checkSelectedAddress = (addressId: number) => {
  return !!(props.modelValue && addressId === props.modelValue.id)
}
</script>

<template>
  <div class="addresses">
    <v-radio-group
      class="radio-group"
      v-bind="$attrs"
      hide-details
      :model-value="activeAddress"
      :true-icon="IconCircle"
      :false-icon="IconRing"
      :ripple="false"
      @update:model-value="change"
    >
      <div
        v-for="(address, index) in addresses"
        :key="address.id"
        class="radio-group__wrapper"
        :class="detectedClassRadio(index)"
      >
        <v-radio
          v-show="index + 1 <= countOfVisibleAddresses"
          class="radio"
          :value="address"
          :class="{
            'radio--disabled':
              deliveryMethod === DeliveryMethods.COURIER &&
              !address.is_in_delivery_area,
          }"
          @click="handleNotDeliveryAddress($event, address)"
        >
          <template #label>
            <template v-if="deliveryMethod != DeliveryMethods.COURIER">
              <slot :address="address" />
            </template>

            <div
              v-if="deliveryMethod === DeliveryMethods.COURIER"
              :class="{ 'edit--mobile': isMobile }"
              class="edit"
            >
              <div class="edit__address">
                <slot :address="address" />
              </div>

              <basket-delivery-address-tag
                class="edit__tag"
                :is-selected-address="checkSelectedAddress(address.id)"
                :is-delivery-area="!!address.is_in_delivery_area"
                :delivery-cost="props.deliveryCost"
              />

              <div class="edit__buttons">
                <v-btn
                  icon
                  class="edit__buttons-remake"
                  @click.stop="openEditAddressDialog(address)"
                >
                  <nuxt-img
                    alt="Редактировать"
                    class="edit__buttons-remake-image"
                    src="/pencil.svg"
                  />
                </v-btn>

                <v-btn
                  class="edit__buttons-delete"
                  icon
                  @click.stop="openDeleteAddressDialog(address)"
                >
                  <icon-close
                    class="edit__button"
                    :color="isDesktop ? '#000' : '#9EA7B6'"
                    :size="{ width: '10px', height: '10px' }"
                  />
                </v-btn>
              </div>
            </div>
          </template>
        </v-radio>
      </div>
    </v-radio-group>

    <app-button
      v-if="showAllButtonVisible"
      :ripple="false"
      class="visibility-addresses"
      variant="text"
      @click="toggleShowAllAddresses"
    >
      <div class="icon" :class="{ 'icon--rotate': showAllAddresses }">
        <icon-chevron-down color="#2c2f39" />
      </div>

      <!--      // TODO: Надо заменить на кнопки v-btn-->
      <span v-if="showAllAddresses">Свернуть</span>
      <span v-else>Показать все адреса</span>
    </app-button>
  </div>
</template>

<style lang="scss" scoped>
:deep(.v-btn--icon) {
  box-shadow: none;
}

:deep(.v-selection-control__input) {
  margin-right: 16px;

  &::before {
    background-color: unset !important;
  }
}

.radio-group {
  margin-top: 0;

  &__wrapper {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;
  }
  .radio {
    align-items: flex-start;
    width: 100%;

    @include media('<=portrait-phone') {
      align-items: flex-start;
    }

    :deep() {
      .v-selection-control__wrapper {
        height: 27px;
      }

      .v-label {
        display: flex;
        justify-content: space-between;
        width: 100%;
        opacity: unset;

        @include media('<=portrait-phone') {
          flex-wrap: wrap;
        }
      }
    }
    // для адресов недоставки
    &--disabled {
      :deep(.address) {
        color: $dark-grey !important;
      }
      label {
        pointer-events: none;
      }
    }

    // для адресов вне видимости
    &--no-visible {
      margin-bottom: 0;
    }
  }
}

.edit {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;

  &__address {
    width: 100%;
  }

  &__tag {
    width: max-content;
    padding: 4px 10px !important;
    margin-right: 16px;
    white-space: nowrap;
  }

  &__buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 27px;

    &-remake {
      display: flex;
      flex: 1 1 auto;
      width: 16px;
      height: 16px;
      margin-right: 22px;
      cursor: pointer;

      &-image {
        width: 16px;
        height: 16px;
        cursor: pointer;
      }
    }

    &-delete {
      width: 16px;
      height: 16px;
      cursor: pointer;

      svg {
        width: 10px;
        height: 10px;
        cursor: pointer;
      }
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &--mobile {
    display: grid;
    grid-template-areas:
      'address buttons'
      'tag tag';
    gap: 8px;

    .edit__address {
      grid-area: address;
    }

    .edit__tag {
      grid-area: tag;
    }

    .edit__buttons {
      grid-area: buttons;
    }
  }
}

.visibility-addresses {
  display: block;
  min-height: unset;
  padding: unset !important;
  font-weight: 700;
  color: $black !important;

  .icon {
    margin-right: 10px;
    transition: all 0.15s ease-in-out;
    &--rotate {
      transform: rotate(-180deg);
    }
  }
}
</style>
